import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getStatusColor = (status) => {
  const colors = {
    DRAFT: 'bg-slate-100 text-slate-700',
    SUBMITTED: 'bg-blue-100 text-blue-700',
    ROUTED_TO_CONSULTANTS: 'bg-cyan-100 text-cyan-700',
    IN_PROGRESS: 'bg-amber-100 text-amber-700',
    AWAITING_CHAIRMAN_ASSIGNMENT: 'bg-purple-100 text-purple-700',
    COORDINATOR_ASSIGNED: 'bg-indigo-100 text-indigo-700',
    PI_ASSIGNED: 'bg-violet-100 text-violet-700',
    INVESTIGATORS_ASSIGNED: 'bg-violet-100 text-violet-700',
    REVIEWERS_ASSIGNED: 'bg-fuchsia-100 text-fuchsia-700',
    UNDER_REVIEW: 'bg-sky-100 text-sky-700',
    CHANGES_REQUESTED: 'bg-orange-100 text-orange-700',
    RESUBMITTED: 'bg-teal-100 text-teal-700',
    PENDING_FINAL_APPROVAL: 'bg-yellow-100 text-yellow-700',
    APPROVED: 'bg-green-100 text-green-700',
    PENDING_DIRECTOR_REVIEW: 'bg-amber-100 text-amber-800',
    PENDING_EXEC_DIRECTOR_REVIEW: 'bg-orange-100 text-orange-800',
    PENDING_CUSTOM_REVIEW: 'bg-amber-100 text-amber-800',
    PENDING_SIGNATURES: 'bg-indigo-100 text-indigo-800',
    READY_TO_EXECUTE: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
    EXECUTED: 'bg-emerald-100 text-emerald-800',
    REJECTED: 'bg-red-100 text-red-700',
    COMPLETED: 'bg-emerald-100 text-emerald-700',
    SIGNED: 'bg-green-100 text-green-700',
    FAILED: 'bg-red-100 text-red-700',
    WITHDRAWN: 'bg-slate-100 text-slate-500',
    // FT (Financial Transaction) statuses
    FT_PENDING_FO_REVIEW: 'bg-amber-100 text-amber-800',
    FT_PENDING_CHAIRMAN_FINAL: 'bg-purple-100 text-purple-700',
    FT_PENDING_FINANCE: 'bg-emerald-100 text-emerald-800',
    FT_PENDING_DIRECTOR: 'bg-orange-100 text-orange-700',
    FT_PENDING_EXEC_DIR_REVIEW: 'bg-red-100 text-red-700',
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

export const getStatusLabel = (status, requestType = null) => {
  // iter166b — Default rename of the SUBMITTED label to "FEA Submitted". By
  // product convention CTAs created by Company Reps move straight from
  // creation to AWAITING_CHAIRMAN_ASSIGNMENT, so the SUBMITTED bucket is
  // FEA-only in practice. The `requestType` arg is still respected (so a
  // future status-rename can be type-specific) but no longer needs to be
  // passed for the FEA Submitted override to apply.
  if (status === 'SUBMITTED') {
    return 'FEA Submitted';
  }
  const labels = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    ROUTED_TO_CONSULTANTS: 'Routed to Consultants',
    IN_PROGRESS: 'In Progress',
    AWAITING_CHAIRMAN_ASSIGNMENT: 'Awaiting Chairman',
    COORDINATOR_ASSIGNED: 'Coordinator Assigned',
    PI_ASSIGNED: 'Investigators Assigned',
    INVESTIGATORS_ASSIGNED: 'Investigators Assigned',
    REVIEWERS_ASSIGNED: 'Reviewers Assigned',
    UNDER_REVIEW: 'Under Review',
    CHANGES_REQUESTED: 'Changes Requested',
    RESUBMITTED: 'Resubmitted',
    PENDING_FINAL_APPROVAL: 'Pending Final Approval',
    APPROVED: 'Approved',
    PENDING_DIRECTOR_REVIEW: 'Pending Director Review',
    PENDING_EXEC_DIRECTOR_REVIEW: 'Pending Exec Director Review',
    PENDING_CUSTOM_REVIEW: 'Pending Reviewer Approval',
    PENDING_SIGNATURES: 'Pending Signatures',
    READY_TO_EXECUTE: 'Ready to Execute',
    EXECUTED: 'Executed',
    REJECTED: 'Rejected',
    COMPLETED: 'Completed',
    SIGNED: 'Signed',
    FAILED: 'Failed',
    WITHDRAWN: 'Withdrawn',
    // FT statuses
    FT_PENDING_FO_REVIEW: 'Pending Financial Officer Review',
    FT_PENDING_CHAIRMAN_FINAL: 'Pending Chairman Approval',
    FT_PENDING_FINANCE: 'Pending Finance Review',
    FT_PENDING_DIRECTOR: 'Pending Director Review',
    FT_PENDING_EXEC_DIR_REVIEW: 'Pending Exec Director Review',
  };
  return labels[status] || status;
};

export const getRoleLabel = (role, requestType = null, user = null) => {
  // Company-account owner login is internally COMPANY_REP but should display as "Company"
  if (role === 'COMPANY_REP' && user?.is_company_owner) {
    return 'Company';
  }
  // Handle INVESTIGATOR role with dynamic labels based on request type
  if (role === 'INVESTIGATOR') {
    if (requestType === 'CTA') {
      return 'Principal Investigator';
    } else if (requestType === 'FEA') {
      return 'Consultant';
    }
    return 'Investigator'; // Default fallback
  }
  
  const labels = {
    COMPANY_REP: 'Company Representative',
    CHAIRMAN: 'Chairman',
    COORDINATOR: 'Coordinator',
    PI: 'Principal Investigator',
    REVIEWER: 'Reviewer',
    CONSULTANT: 'Consultant',
    CRD: 'Clinical Research Dept',
    ADMIN: 'Administrator',
    DIRECTOR: 'Director',
    EXEC_DIRECTOR: 'Executive Director',
    FINANCE: 'Finance',
    FINANCIAL_OFFICER: 'Financial Officer',
    // iter147 — automated thread author surfaces as "ClinicalFlow Agent" (renamed from "AI Agent").
    COORDINATOR_AGENT: 'ClinicalFlow Agent',
    SYSTEM: 'System',
  };
  return labels[role] || role;
};

// Helper to get investigator label based on request type and whether they're a Co-PI
export const getInvestigatorLabel = (requestType, isCo = false) => {
  if (requestType === 'CTA') {
    return isCo ? 'Co-Principal Investigator' : 'Principal Investigator';
  }
  return 'Consultant'; // FEA doesn't have Co- designation
};

export const getRoleColor = (role, user = null) => {
  // Company-account owner: distinct golden-yellow badge to differentiate from regular reps
  if (role === 'COMPANY_REP' && user?.is_company_owner) {
    return 'bg-amber-100 text-amber-800 border border-amber-300';
  }
  // Map INVESTIGATOR to use the same color scheme
  const effectiveRole = role === 'INVESTIGATOR' ? 'PI' : role;
  
  const colors = {
    COMPANY_REP: 'bg-emerald-100 text-emerald-700',
    CHAIRMAN: 'bg-purple-100 text-purple-700',
    COORDINATOR: 'bg-blue-100 text-blue-700',
    PI: 'bg-indigo-100 text-indigo-700',
    REVIEWER: 'bg-cyan-100 text-cyan-700',
    CONSULTANT: 'bg-amber-100 text-amber-700',
    CRD: 'bg-rose-100 text-rose-700',
    ADMIN: 'bg-slate-800 text-white',
    DIRECTOR: 'bg-orange-100 text-orange-700',
    EXEC_DIRECTOR: 'bg-red-100 text-red-700',
    FINANCE: 'bg-emerald-100 text-emerald-700',
    // iter138 — AI-feel gradient pill for the autonomous agent.
    COORDINATOR_AGENT: 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 text-white border-0 shadow-sm',
    SYSTEM: 'bg-slate-200 text-slate-700',
  };
  return colors[role] || 'bg-slate-100 text-slate-700';
};

export const getTypeColor = (type) => {
  if (type === 'FEA') return 'bg-teal-100 text-teal-700';
  if (type === 'FT') return 'bg-emerald-100 text-emerald-700';
  return 'bg-violet-100 text-violet-700';  // CTA default
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Image extensions the platform supports for e-signature / bundling.
// On the backend each image is wrapped into a centred-fit A4 PDF page on demand.
export const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.tiff', '.tif', '.bmp', '.heic', '.heif'];

export const isImageFilename = (filename) => {
  if (!filename) return false;
  const lower = String(filename).toLowerCase();
  return IMAGE_EXTENSIONS.some(ext => lower.endsWith(ext));
};

